import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import { getAuthHeader } from '../utils/auth'; // ★ 1. 認証ヘッダー取得関数をインポート
import { isEnglishOperatorMode, withOperatorSurface } from '../utils/operatorMode';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

function ListTemplatesPage({ routeBase = null }) {
    const { user, clientId } = useOutletContext();
    const [lists, setLists] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const listRoute = routeBase || withOperatorSurface("/list-templates");
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = isEnglishBeta ? {
        title: "Recipient Lists",
        newList: "Create Recipient List",
        loading: "Loading recipient lists...",
        empty: "No recipient lists yet.",
        noDescription: "No description",
        listName: "List Name",
        description: "Description",
        targetCount: "Companies",
        updatedAt: "Last Updated",
        actions: "Actions",
        edit: "Edit",
        delete: "Delete",
        countSuffix: "companies",
        deleteConfirm: "Delete this recipient list?",
        deleteSuccess: "Recipient list deleted.",
        deleteFailure: "Delete failed.",
        fetchFailure: "Could not load recipient lists: ",
        networkFailure: "A network error occurred.",
        errorPrefix: "Error: ",
        dateLocale: "en-US",
    } : {
        title: "送付先リストテンプレート",
        newList: "新規リスト作成",
        loading: "読み込み中...",
        empty: "リストテンプレートはありません。",
        noDescription: "説明なし",
        listName: "リスト名",
        description: "説明",
        targetCount: "企業数",
        updatedAt: "最終更新日",
        actions: "操作",
        edit: "編集",
        delete: "削除",
        countSuffix: "件",
        deleteConfirm: "このリストテンプレートを本当に削除しますか？",
        deleteSuccess: "リストを削除しました。",
        deleteFailure: "削除に失敗しました。",
        fetchFailure: "リストの取得に失敗しました: ",
        networkFailure: "通信エラーが発生しました。",
        errorPrefix: "エラー: ",
        dateLocale: "ja-JP",
    };

    const formatTargetCount = (count) =>
        isEnglishBeta ? `${Number(count || 0).toLocaleString()} ${copy.countSuffix}` : `${Number(count || 0).toLocaleString()}${copy.countSuffix}`;

    const fetchLists = useCallback(async () => {
        if (!clientId) return;
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/saved-lists?client_id=${clientId}`, {
                headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
            });
            const data = await res.json();
            if (data.success) {
                setLists(data.lists || []);
            } else {
                alert(copy.fetchFailure + data.error);
            }
        } catch (error) {
            console.error("リスト取得エラー:", error);
            alert(copy.networkFailure);
        } finally {
            setIsLoading(false);
        }
    }, [clientId, copy.fetchFailure, copy.networkFailure]);

    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

    const handleDelete = async (id) => {
        if (!window.confirm(copy.deleteConfirm)) return;
        try {
            const res = await fetch(`${API_URL}/api/saved-lists/${id}`, {
                method: 'DELETE',
                headers: getAuthHeader(), // ★ 2. 認証ヘッダーを追加
            });
            if (!res.ok) throw new Error(copy.deleteFailure);
            alert(copy.deleteSuccess);
            fetchLists();
        } catch (error) {
            alert(`${copy.errorPrefix}${error.message}`);
        }
    };

    return (
        <div className="p-3 sm:p-4 md:p-8 bg-gray-50 min-h-full">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{copy.title}</h1>
                    <Link to={`${listRoute}/new`} className="w-full rounded-md bg-blue-600 px-4 py-2 text-center font-semibold text-white hover:bg-blue-700 sm:w-auto">
                        {copy.newList}
                    </Link>
                </div>
                
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {isLoading ? (
                        <p className="p-8 text-center">{copy.loading}</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <div className="divide-y divide-gray-100 md:hidden">
                                {lists.length > 0 ? lists.map(list => (
                                    <div key={`${list.id}-mobile`} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-900 break-words">{list.name}</p>
                                                <p className="mt-1 text-xs text-gray-500">{new Date(list.updated_at).toLocaleDateString(copy.dateLocale)}</p>
                                            </div>
                                            <span className="flex-shrink-0 rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">{formatTargetCount(list.target_count)}</span>
                                        </div>
                                        <p className="mt-3 text-sm text-gray-600 break-words">{list.description || copy.noDescription}</p>
                                        <div className="mt-4 grid grid-cols-2 gap-2">
                                            <button onClick={() => navigate(`${listRoute}/edit/${list.id}`)} className="rounded-md bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100">{copy.edit}</button>
                                            <button onClick={() => handleDelete(list.id)} className="rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100">{copy.delete}</button>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="px-4 py-10 text-center text-sm text-gray-500">{copy.empty}</div>
                                )}
                            </div>
                            <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                                <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                                    <tr>
                                        <th className="py-3 px-4">{copy.listName}</th>
                                        <th className="py-3 px-4">{copy.description}</th>
                                        <th className="py-3 px-4">{copy.targetCount}</th>
                                        <th className="py-3 px-4">{copy.updatedAt}</th>
                                        <th className="py-3 px-4">{copy.actions}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {lists.length > 0 ? lists.map(list => (
                                        <tr key={list.id}>
                                            <td className="py-3 px-4 font-semibold">{list.name}</td>
                                            <td className="py-3 px-4">{list.description || copy.noDescription}</td>
                                            <td className="py-3 px-4">{formatTargetCount(list.target_count)}</td>
                                            <td className="py-3 px-4">{new Date(list.updated_at).toLocaleDateString(copy.dateLocale)}</td>
                                            <td className="py-3 px-4 space-x-2">
                                                <button onClick={() => navigate(`${listRoute}/edit/${list.id}`)} className="text-indigo-600 hover:underline">{copy.edit}</button>
                                                <button onClick={() => handleDelete(list.id)} className="text-red-600 hover:underline">{copy.delete}</button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="5" className="py-10 text-center text-gray-500">{copy.empty}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ListTemplatesPage;
